import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  // Pre-populate demo credentials so judges can sign in with a single click.
  const [email, setEmail] = useState("VELQUE@gmail.com");
  const [password, setPassword] = useState("Demo@123");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout eyebrow="Welcome back" title="Sign in to VELQUE" subtitle="Your AI executive has been waiting.">
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="lux-label">Work email</label>
          <input
            data-testid="login-email-input"
            className="lux-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="lux-label">Password</label>
            <Link data-testid="login-forgot-link" to="/forgot-password" className="text-base text-nexa-mute hover:text-[#2E7D88]">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              data-testid="login-password-input"
              className="lux-input pr-11"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-nexa-mute hover:text-nexa-ink"
              aria-label="Toggle password"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="login-error" className="text-[15px] text-[#EF4444] bg-[#EF4444]/8 border border-[#EF4444]/20 rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        <button data-testid="login-submit-btn" className="lux-btn-primary w-full flex items-center justify-center" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center"><div className="w-full h-px  bg-[#D4A017]/20 bg-nexa-line"/></div>
          <div className="relative flex justify-center"><span className="bg-white px-3 text-base text-nexa-mute uppercase tracking-widest">or</span></div>
        </div>

        <button
          type="button"
          data-testid="login-google-btn"
          onClick={googleSignIn}
          className="lux-btn-secondary w-full flex items-center justify-center gap-3"
        >
          <GoogleGlyph /> Continue with Google
        </button>

        <p className="text-center text-[15px] text-nexa-mute pt-2">
          Don't have an account?{" "}
          <Link data-testid="login-signup-link" to="/signup" className="lux-link">Create one</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.9 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-1.9 13.6-5.1l-6.3-5.2C29.3 34.9 26.8 36 24 36c-5.4 0-9.9-3.1-11.7-7.6l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.3 5.2C41 34.9 44 29.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
